export type Provider = 'gemini';
export type DeliveryMode = 'nocturne-tray' | 'download';

export interface Machine {
  name: string;
  workerUrl: string;
  bearerToken: string;
}

export interface Settings {
  provider: Provider;
  apiKey: string;
  deliveryMode: DeliveryMode;
  machines: Machine[];
  books: string[];
}

const STORAGE_KEY = 'nocturne-handwriter.settings';

const FRESH_DEFAULTS: Settings = {
  provider: 'gemini',
  apiKey: '',
  deliveryMode: 'download',
  machines: [],
  books: [],
};

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...FRESH_DEFAULTS };
    const parsed = JSON.parse(raw) as unknown;
    return mergeWithDefaults(parsed);
  } catch {
    return { ...FRESH_DEFAULTS };
  }
}

export function saveSettings(settings: Settings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

function mergeWithDefaults(value: unknown): Settings {
  if (typeof value !== 'object' || value === null) return { ...FRESH_DEFAULTS };
  const v = value as Record<string, unknown>;
  const machines = Array.isArray(v.machines) ? v.machines.filter(isMachine) : [];
  const books = Array.isArray(v.books)
    ? v.books.filter((b): b is string => typeof b === 'string')
    : [];
  return {
    provider: 'gemini',
    apiKey: typeof v.apiKey === 'string' ? v.apiKey : '',
    deliveryMode: resolveDeliveryMode(v.deliveryMode, machines, books),
    machines,
    books,
  };
}

// Pre-migration users have no saved deliveryMode. Infer from their
// existing data: any named machine or book means they're an existing
// Nocturne user and should stay on tray mode; otherwise default to
// download so a first-time install on a fresh device just works.
function resolveDeliveryMode(
  raw: unknown,
  machines: Machine[],
  books: string[],
): DeliveryMode {
  if (raw === 'nocturne-tray' || raw === 'download') return raw;
  const hasNocturneData =
    machines.some((m) => m.name.length > 0) || books.some((b) => b.length > 0);
  return hasNocturneData ? 'nocturne-tray' : 'download';
}

function isMachine(value: unknown): value is Machine {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === 'string' &&
    typeof v.workerUrl === 'string' &&
    typeof v.bearerToken === 'string'
  );
}
